import { append, el, type Child } from './dom';
import { icon, type IconName } from './Icons';

/**
 * Shared modal shell for the project menus (new site, save, open).
 *
 * One shell rather than three bespoke overlays: every dialog then gets the same header, the same
 * footer button order, the same Escape/backdrop dismissal, and the same panel surface as the docks —
 * which is what "consistent UI" has to mean for something the user reaches from the toolbar.
 *
 * Dialogs mount inside the HUD root so they inherit its theme variables and disappear with it in
 * photo mode. Keyboard events are stopped at the dialog: the editor listens on `window`, and a
 * stray `P` while a Save button has focus must not arm the planting tool.
 */

export interface DialogDeps {
  /** Element to mount into — the HUD root, so the dialog inherits the HUD theme. */
  host: HTMLElement;
  title: string;
  icon: IconName;
  subtitle?: string;
  /** Called after the dialog closes, however it was dismissed. */
  onClose?: () => void;
}

export class Dialog {
  readonly root: HTMLElement;
  readonly body: HTMLElement;

  private readonly panel: HTMLElement;
  private readonly subtitleEl: HTMLElement;
  private readonly footer: HTMLElement;
  private previousFocus: HTMLElement | null = null;
  private open = false;

  constructor(private readonly deps: DialogDeps) {
    this.body = el('div', { class: 'lg-dialog__body' });
    this.footer = el('div', { class: 'lg-dialog__foot' });
    this.subtitleEl = el('span', {
      class: 'lg-dialog__subtitle',
      text: deps.subtitle ?? '',
    });

    this.panel = el(
      'div',
      {
        class: 'lg-dialog',
        attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': deps.title },
      },
      el(
        'div',
        { class: 'lg-panel__head lg-dialog__head' },
        el('span', { class: 'lg-panel__icon', html: icon(deps.icon, 16) }),
        el(
          'div',
          { class: 'lg-dialog__heading' },
          el('h2', { class: 'lg-panel__title', text: deps.title }),
          this.subtitleEl,
        ),
        el('button', {
          class: 'lg-panel__collapse',
          html: icon('close', 16),
          title: 'Close',
          attrs: { type: 'button', 'aria-label': 'Close' },
          on: { click: () => this.close() },
        }),
      ),
      this.body,
      this.footer,
    );

    this.root = el(
      'div',
      { class: 'lg-modal' },
      this.panel,
    );
    this.root.addEventListener('pointerdown', (event) => {
      // Dismiss only when the press *starts* on the backdrop: a drag begun inside the dialog (text
      // selection, slider) targets the panel, so it can never close the dialog under the user's hand.
      if (event.target === this.root) this.close();
    });
    this.root.addEventListener('keydown', (event) => {
      event.stopPropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    });
  }

  get isOpen(): boolean {
    return this.open;
  }

  setSubtitle(text: string): void {
    this.subtitleEl.textContent = text;
  }

  /** Replace the footer buttons. Primary action goes last, matching the platform convention. */
  setActions(...children: Child[]): void {
    this.footer.replaceChildren();
    append(this.footer, ...children);
  }

  show(): void {
    if (this.open) return;
    this.open = true;
    this.previousFocus = document.activeElement as HTMLElement | null;
    this.deps.host.appendChild(this.root);
    // Focus the first control so the dialog is immediately keyboard-operable.
    const focusable = this.panel.querySelector<HTMLElement>(
      'input, select, textarea, button:not([data-autofocus="skip"])',
    );
    focusable?.focus();
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.root.remove();
    this.previousFocus?.focus?.();
    this.previousFocus = null;
    this.deps.onClose?.();
  }

  dispose(): void {
    this.open = false;
    this.root.remove();
  }
}

/* ------------------------------ Field builders ------------------------------ */

/** A labelled row: label on the left, control on the right, optional hint underneath. */
export function dialogField(label: string, control: Child, hint?: string): HTMLElement {
  return el(
    'div',
    { class: 'lg-dialog__field' },
    el(
      'div',
      { class: 'lg-dialog__fieldrow' },
      el('span', { class: 'lg-field__label', text: label }),
      control,
    ),
    hint !== undefined ? el('span', { class: 'lg-dialog__hint', text: hint }) : null,
  );
}

/**
 * One label/value line of a summary card — the account dialog's and the info sheet's alike.
 *
 * Shared rather than restated in each, because two cards that are meant to look like one card have
 * to be built from one function; the moment they are not, one of them grows a different padding.
 */
export function dialogSummaryRow(label: string, value: string): HTMLElement {
  return el(
    'div',
    { class: 'lg-dialog__summaryrow' },
    el('span', { class: 'lg-dialog__summarylabel', text: label }),
    el('span', { class: 'lg-dialog__summaryvalue', text: value }),
  );
}

/** A section heading inside a dialog body. */
export function dialogSection(title: string): HTMLElement {
  return el('h3', { class: 'lg-section__title', text: title });
}

export interface TextInputOptions {
  value?: string;
  placeholder?: string;
  ariaLabel: string;
  onInput?: (value: string) => void;
  /** Enter in a text field triggers the dialog's primary action. */
  onSubmit?: () => void;
}

export function textInput(options: TextInputOptions): HTMLInputElement {
  return el('input', {
    class: 'lg-input',
    attrs: {
      type: 'text',
      value: options.value ?? '',
      placeholder: options.placeholder ?? '',
      'aria-label': options.ariaLabel,
      spellcheck: 'false',
    },
    on: {
      input: (event) => options.onInput?.((event.target as HTMLInputElement).value),
      keydown: (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          options.onSubmit?.();
        }
      },
    },
  });
}

export interface NumberInputOptions {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  ariaLabel: string;
  onChange: (value: number) => void;
}

export function numberInput(options: NumberInputOptions): HTMLInputElement {
  return el('input', {
    class: 'lg-input lg-input--number',
    attrs: {
      type: 'number',
      value: String(options.value),
      min: options.min !== undefined ? String(options.min) : false,
      max: options.max !== undefined ? String(options.max) : false,
      step: String(options.step ?? 1),
      'aria-label': options.ariaLabel,
    },
    on: {
      change: (event) => options.onChange(Number((event.target as HTMLInputElement).value)),
    },
  });
}

export interface DialogButtonOptions {
  label: string;
  onClick: () => void;
  primary?: boolean;
  danger?: boolean;
  icon?: IconName;
  title?: string;
  /** Set at construction, for a button whose availability is decided by the render that made it. */
  disabled?: boolean;
}

export function dialogButton(options: DialogButtonOptions): HTMLButtonElement {
  const classes = ['lg-button'];
  if (options.primary) classes.push('lg-button--primary');
  if (options.danger) classes.push('lg-button--danger');
  const button = el('button', {
    class: classes.join(' '),
    attrs: { type: 'button', 'data-autofocus': 'skip' },
    ...(options.title !== undefined ? { title: options.title } : {}),
    on: { click: () => options.onClick() },
  });
  if (options.disabled === true) button.disabled = true;
  if (options.icon) {
    button.appendChild(el('span', { class: 'lg-button__icon', html: icon(options.icon, 15) }));
  }
  button.appendChild(el('span', { text: options.label }));
  return button;
}

/** A segmented control (mirrors the environment tray's mode switch). */
export function dialogSegment(
  ariaLabel: string,
  items: readonly { id: string; label: string }[],
  active: string,
  onPick: (id: string) => void,
): HTMLElement {
  const row = el('div', { class: 'lg-segment', attrs: { role: 'group', 'aria-label': ariaLabel } });
  for (const item of items) {
    row.appendChild(
      el('button', {
        class: `lg-segment__item${item.id === active ? ' is-active' : ''}`,
        text: item.label,
        attrs: { type: 'button', 'aria-pressed': item.id === active, 'data-autofocus': 'skip' },
        on: { click: () => onPick(item.id) },
      }),
    );
  }
  return row;
}
